import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "crop-succession-scheduling",
  kind: "domain-vertical",
  title: "Crop Succession & Rotation Scheduling",
  shortTitle: "Succession Scheduling",
  status: "draft",
  tier: "we-do",
  seoTitle: "Crop Succession & Rotation Scheduling | GA",
  seoDescription:
    "Succession planting engines, botanical family rotation rules, days-to-maturity tracking, and harvest projection schedules for commercial growers.",
  summary:
    "Succession planting engines, botanical crop rotation rules, and harvest projections that coordinate multi-season planting schedules without spreadsheet collapse.",
  image: "/images/blog/operational-task-management-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["agriculture"],
  relatedPostSlugs: [
    "operational-task-management",
    "reconciling-fragmented-data",
    "succession-calendars-living-inventories",
  ],
  engineering: [
    "Continuous succession planting engines computing target seeding, planting, and harvest windows",
    "Multi-year botanical family crop rotation engines preventing soil depletion and pathogen carryover",
    "Dynamic date-offset calculation based on spring and fall frost dates, photoperiod, and days-to-maturity",
    "Bed turnover scheduling calculating clearing, bed preparation, and re-planting turnaround windows",
    "Cascading schedule adjustment engines that propagate weather delays through downstream successions",
    "Seasonal yield target modeling linking sales projections to required planting successions and bed feet",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "The Planting Schedule Is a Cascading Dependency Graph",
    },
    {
      type: "paragraph",
      text: "Intensive commercial growing demands coordinating dozens of consecutive planting successions from early spring through late fall. When spring frost delays field preparation or an unseasonal heat wave accelerates crop maturity, every downstream planting date, bed turnover window, and harvest projection shifts. In traditional spreadsheets, a single schedule adjustment breaks hardcoded formulas and produces conflicting bed assignments.",
    },
    {
      type: "heading",
      level: 2,
      text: "Enforcing Biological Rules Across Multi-Year Horizons",
    },
    {
      type: "paragraph",
      text: "A dependable succession system models the farm schedule as an explicit dependency graph constrained by biological rules. By verifying botanical family crop rotation intervals across specific beds, accounting for seasonal photoperiod fluctuations, and automating bed turnover buffers, the software ensures that scheduling changes adapt predictably rather than deteriorating into chaos.",
    },
  ],
  phases: {
    study: {
      focus:
        "Analyze historical planting schedules, crop successions, target harvest windows, and crop rotation rules.",
      produces: [
        "Crop succession inventory, target harvest dates, and yield requirements",
        "Botanical family rotation matrix and minimum multi-year interval specifications",
        "Bed turnover timing and soil preparation buffer requirements",
        "Historical scheduling bottleneck and seasonal labor constraint analysis",
      ],
      fit: {
        good: [
          "Commercial market growers, diversified vegetable farms, and CSA producers managing multi-crop succession schedules",
          "Operations wanting automated rotation enforcement and schedule adjustment when weather events occur",
        ],
        poor: [
          "Single-season monoculture operations with no succession planting or complex rotation needs",
          "Farms seeking static calendar software without crop maturity logic or biological constraints",
        ],
      },
    },
    "initial-design": {
      focus:
        "Model succession schedule state machines, date-offset algorithms, rotation rule validators, and timeline views.",
      produces: [
        "Succession scheduling data models and date-calculation algorithms",
        "Botanical family rotation rule engine and conflict-flagging models",
        "Bed turnover and bed-occupancy timeline schemas",
        "Seasonal planting calendar and succession Gantt interface wireframes",
      ],
    },
    "detailed-design": {
      focus:
        "Specify dynamic date propagation rules, sales-to-bed calculation formulas, and schedule adjustment APIs.",
      produces: [
        "Cascading schedule adjustment engine specifications and delay handling logic",
        "Yield target to bed-foot conversion formulas and seed quantity models",
        "Bed conflict resolution algorithms and re-allocation contracts",
        "Calendar synchronization and scheduling export endpoints",
      ],
    },
    implementation: {
      focus:
        "Build and deploy the succession scheduling system, configure rotation rules, and import seasonal crop plans.",
      produces: [
        "Production crop succession and rotation scheduling application",
        "Configured multi-year rotation rule engines and family classification trees",
        "Imported seasonal crop successions and validated bed assignment plans",
        "Operational scheduling runbooks and seasonal revision guidelines",
      ],
    },
  },
};
